"use client";

import { BlogPreviewGrid } from "@/components/blog/BlogPreviewGrid";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Pagination } from "@/components/ui/Pagination";
import type { Blog, BlogPost } from "@/types";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { useMemo, useRef, useState } from "react";

const PAGE_SIZE = 8;

type BlogArchiveProps = {
  blog: Blog;
  posts: BlogPost[];
  basePath: string;
};

function normalizeTitle(value: string) {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("cs-CZ");
}

export function BlogArchive({ blog, posts, basePath }: BlogArchiveProps) {
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const resultsRef = useRef<HTMLDivElement>(null);
  const filteredPosts = useMemo(() => {
    const normalizedQuery = normalizeTitle(query.trim());

    if (!normalizedQuery) return posts;

    return posts.filter((post) =>
      normalizeTitle(post.title).includes(normalizedQuery),
    );
  }, [posts, query]);
  const totalPages = Math.max(1, Math.ceil(filteredPosts.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const visiblePosts = filteredPosts.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );

  const updateQuery = (value: string) => {
    setQuery(value);
    setPage(1);
  };

  const updatePage = (nextPage: number) => {
    setPage(nextPage);
    window.requestAnimationFrame(() => {
      resultsRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  };

  return (
    <section aria-label="Přehled článků">
      <div className="mb-8 flex flex-col gap-3 border-y border-softiq-border-subtle py-5 sm:flex-row sm:items-center sm:justify-between dark:border-softiq-border-dark">
        <div className="relative w-full sm:max-w-md">
          <MagnifyingGlassIcon
            aria-hidden
            className="pointer-events-none absolute left-3 top-1/2 z-50 size-4 -translate-y-1/2 text-foreground/50"
          />
          <Input
            type="search"
            value={query}
            onChange={(event) => updateQuery(event.target.value)}
            placeholder="Hledat podle názvu článku"
            aria-label="Hledat podle názvu článku"
            className="mb-0 pl-10"
          />
        </div>
        <p className="shrink-0 text-sm text-foreground/60" aria-live="polite">
          {filteredPosts.length}{" "}
          {filteredPosts.length === 1 ? "článek" : "článků"}
        </p>
      </div>

      {visiblePosts.length ? (
        <>
          <div ref={resultsRef} className="scroll-mt-24">
            <BlogPreviewGrid
              blog={blog}
              posts={visiblePosts}
              basePath={basePath}
              minimumItems={PAGE_SIZE}
            />
          </div>
          <Pagination
            page={currentPage}
            totalPages={totalPages}
            setPage={updatePage}
          />
        </>
      ) : (
        <div className="py-14 text-center">
          <p className="text-lg font-medium text-foreground">
            Žádný článek tomuto názvu neodpovídá.
          </p>
          <Button
            type="button"
            variant="minimal"
            className="mt-4"
            onClick={() => updateQuery("")}
          >
            Vymazat hledání
          </Button>
        </div>
      )}
    </section>
  );
}
